import { z } from "zod";

export const UserRole = z.enum(["admin", "office_staff", "driver"]);
export type UserRole = z.infer<typeof UserRole>;

/**
 * ドライバー(担当者)として稼働・割当できるロール。
 * admin・office_staff(経理/事務) は運用上ドライバーとしても稼働することがあるため、driver と並んで
 * 担当者候補・割当対象になる。配送ルート割当・ドライバー一覧・driver アプリのログイン可否で参照する。
 */
export const DRIVER_CAPABLE_ROLES: readonly UserRole[] = ["driver", "admin", "office_staff"];

export function isDriverCapableRole(role: UserRole): boolean {
  return DRIVER_CAPABLE_ROLES.includes(role);
}

export const UserStatus = z.enum(["pending", "active", "inactive"]);
export type UserStatus = z.infer<typeof UserStatus>;

/**
 * 雇用形態 (ドライバー向け)。運転日報の休憩時間算出ルール分岐に使う。
 * - full_time (正社員): 労基法休憩に加え、改善基準告示の「430 休憩」(4 時間ごと 30 分) を考慮し大きい方を採用
 * - contractor (業務委託) / part_time (アルバイト): 労基法休憩のみ
 * admin/office_staff など非ドライバーは null。
 */
export const EmploymentType = z.enum(["full_time", "contractor", "part_time"]);
export type EmploymentType = z.infer<typeof EmploymentType>;

export const TimeSlot = z.enum(["early_morning", "am", "pm"]);
export type TimeSlot = z.infer<typeof TimeSlot>;

export const OrderStatus = z.enum(["draft", "confirmed", "assigned", "completed", "cancelled"]);
export type OrderStatus = z.infer<typeof OrderStatus>;

/**
 * 経由地 (自社倉庫への立寄り等) での作業種別 (#722)。
 * 案件によって「積む」「降ろす」「立ち寄るだけ」のいずれもありうるため経由地ごとに選ぶ。
 * DB 側の単一源泉は packages/database の WAYPOINT_ACTIONS (order_locations.waypoint_action)。
 */
export const WaypointAction = z.enum(["load", "unload", "none"]);
export type WaypointAction = z.infer<typeof WaypointAction>;

/**
 * 経由地の依頼元 (#808)。値の単一源泉は packages/database の WAYPOINT_REQUESTERS。
 * own=自社都合、client=その注文と同じ取引先の指示。
 */
export const WaypointRequester = z.enum(["own", "client"]);
export type WaypointRequester = z.infer<typeof WaypointRequester>;

/**
 * 注文ステータスの手動遷移グラフ (単一の真実)。
 * draft→confirmed、cancelled→draft の復活、completed/cancelled 以外からの cancelled を許可する。
 * confirmed→assigned はルートへの組み込み時、assigned→completed は全 delivery stop 完了時に
 * システムが自動遷移させるため、手動遷移には含めない。
 * 配送の進行中状態は orders ではなく route_stops / 配送計画 (DELIVERY_PLAN_STATUSES) で管理するため、
 * orders に in_progress は持たせない。Record<OrderStatus, ...> によりステータス追加時の網羅を型強制する。
 */
export const ORDER_STATUS_TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  draft: ["confirmed", "cancelled"],
  confirmed: ["cancelled"],
  assigned: ["cancelled"],
  completed: [],
  cancelled: ["draft"],
};

/**
 * `from` から `to` への注文ステータス遷移が許可されているかを返す。
 * 同一ステータス (from === to) は遷移ではないため false を返す。
 */
export function canTransitionOrderStatus(from: OrderStatus, to: OrderStatus): boolean {
  return ORDER_STATUS_TRANSITIONS[from].includes(to);
}

/**
 * 内容編集をロックするステータス (Issue #347)。
 * - assigned (配車済): ルートに紐付いており、内容を変えると order と route の delivery_date が
 *   ズレて幽霊 stop が残る。配車解除 (route.removeOrder → confirmed) してから編集する。
 * - completed / cancelled: 確定した記録なので内容編集しない。
 * ステータス遷移そのもの (order.updateStatus) はこの制限の対象外。
 */
export const ORDER_CONTENT_LOCKED_STATUSES = ["assigned", "completed", "cancelled"] as const;

/** 受注の内容編集 (order.update / 受注編集フォーム) がロックされるステータスか。 */
export function isOrderContentLocked(status: OrderStatus): boolean {
  return (ORDER_CONTENT_LOCKED_STATUSES as readonly OrderStatus[]).includes(status);
}

/** ロック時に表示する理由メッセージ (画面・API で共通)。ロック外は null。 */
export function orderContentLockReason(status: OrderStatus): string | null {
  if (status === "assigned") {
    return "配車済の受注は内容を変更できません。配車画面でレーンから取り外して配車を解除してから編集してください。";
  }
  if (status === "completed") return "完了済みの受注は内容を変更できません。";
  if (status === "cancelled") return "キャンセル済みの受注は内容を変更できません。";
  return null;
}

export const OrderSourceType = z.enum(["manual", "pdf", "api"]);
export type OrderSourceType = z.infer<typeof OrderSourceType>;

export const ParseMethod = z.enum(["local", "gemini", "manual"]);
export type ParseMethod = z.infer<typeof ParseMethod>;

export const DeliveryStatus = z.enum([
  "pending",
  "in_transit",
  "arrived",
  "completed",
  "skipped",
  "cancelled",
]);
export type DeliveryStatus = z.infer<typeof DeliveryStatus>;

/**
 * #769: 過去日の配送実績の後追い登録 (申請) の状態。
 * pending = 承認待ち / approved = 承認され実績へ反映済み / rejected = 却下 / cancelled = 申請者が取下げ。
 */
export const BackfillRequestStatus = z.enum(["pending", "approved", "rejected", "cancelled"]);
export type BackfillRequestStatus = z.infer<typeof BackfillRequestStatus>;

/**
 * #769: 後追い登録を拒否する理由。文言は labels.ts が単一ソース。
 * - not_past_date: 対象日が JST 当日・未来日。通常の打刻フローを使う
 * - report_locked: 承認済みの運転日報に含まれる日
 * - invoice_locked: 発行済み (issued/sent/paid) の請求書に含まれる月
 * - route_editing: レーンが配車担当の編集中
 * - already_finalized: 既に completed / skipped / cancelled
 * - time_out_of_work_date: 入力時刻が対象日 (JST) の範囲外
 * - arrival_after_departure: 到着時刻が完了時刻より後
 */
export const BackfillBlockedReason = z.enum([
  "not_past_date",
  "report_locked",
  "invoice_locked",
  "route_editing",
  "already_finalized",
  "time_out_of_work_date",
  "arrival_after_departure",
]);
export type BackfillBlockedReason = z.infer<typeof BackfillBlockedReason>;

export const StopType = z.enum([
  "pickup",
  "delivery",
  // 注文の経由地 (自社倉庫への立寄り等)。同じ車が続けて回る点が cross_dock_* と異なる (#724)。
  "waypoint",
  "cross_dock_drop",
  "cross_dock_pickup",
  "return",
]);
export type StopType = z.infer<typeof StopType>;

/**
 * route_proposals の業務状態 (提案ライフサイクル)。
 * draft (ジョブ完了直後) → adjusted (手動調整) → confirmed (確定) → executed (実行済み)。
 * 最適化ジョブの実行状態 (queued/running/completed/failed) は別軸 RouteProposalJobStatus で管理する。
 */
export const RouteProposalStatus = z.enum(["draft", "adjusted", "confirmed", "executed"]);
export type RouteProposalStatus = z.infer<typeof RouteProposalStatus>;

/**
 * route_proposals の最適化ジョブ状態 (ジョブライフサイクル)。
 * queued (作成直後) → running (optimizer 呼出中) → completed (結果保存済み) / failed (失敗)。
 * 提案の業務状態 (draft/adjusted/...) とは独立した軸で、SSE はこの状態で進捗・完了・失敗を判定する。
 */
export const RouteProposalJobStatus = z.enum(["queued", "running", "completed", "failed"]);
export type RouteProposalJobStatus = z.infer<typeof RouteProposalJobStatus>;

export const AdjustmentType = z.enum(["reorder", "reassign", "add_stop", "remove_stop"]);
export type AdjustmentType = z.infer<typeof AdjustmentType>;

export const AdjustmentReasonCode = z.enum([
  "time_mismatch",
  "delivery_request",
  "driver_preference",
  "traffic",
  "other",
]);
export type AdjustmentReasonCode = z.infer<typeof AdjustmentReasonCode>;

export const InviteCodeStatus = z.enum(["active", "used", "expired", "revoked"]);
export type InviteCodeStatus = z.infer<typeof InviteCodeStatus>;
